import { Product } from 'schema-dts';
import { ReviewRatingStatistics, showsLeadTime } from 'shared/types/product/Product';
import { TRANSIT_DAYS } from 'shared/types/product/shipping';
import { ExtendedVariant } from 'shared/types/product/Variant';
import { mapReturnPolicy } from "./returnPolicy";
import { cloudinaryLoader } from '../../../components/commercetools-ui/atoms/image/loaders/cloudinary';
import { UIProduct } from '../../../components/commercetools-ui/organisms/product/product-details/types';
import { ReviewListProps } from '../../../components/commercetools-ui/organisms/review-list';
import { DEFAULT_CURRENCY } from "../../constants/ecommerce";
import { variantPath } from '../../seo/variant-seo';

export const mapProductImages = (product: UIProduct) => {
  return product.images.reduce((previousValue, currentValue) => {
    if (currentValue?.media?.mediaId) {
      previousValue.push(
        cloudinaryLoader({
          mediaId: currentValue?.media?.mediaId,
          src: currentValue?.src ?? '',
          width: 500,
        }),
      );
    } else if (currentValue?.src) {
      // Non-Cloudinary image - include as-is
      previousValue.push(currentValue.src);
    }
    return previousValue;
  }, [] as string[]);
};

export const mapProductRatings = (ratingData: ReviewRatingStatistics): Product['aggregateRating'] => {
  return {
    '@type': 'AggregateRating',
    ratingValue: ratingData.averageRating,
    reviewCount: ratingData.count,
  };
};

/* Checkout cannot book R&L freight to Alaska, Hawaii or the territories (OffShoreZipCodes), so a
   freight transit window is only promised for the contiguous states. OffShoreZipCodes also lists some
   islands inside these states (e.g. Nantucket, the Keys), which a state list cannot exclude. */
const CONTIGUOUS_US_STATES = [
  'AL', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA',
  'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH',
  'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

export const mapProductOffer = (
  product: UIProduct,
  variant: ExtendedVariant,
  finalBasePrice: number,
  origin?: string,
): Product['offers'] => {
  const returnPolicy = mapReturnPolicy(product, variant);
  /* shippingClass is set by the backend ProductMapper. Made-to-order items ("quoted") have no
     dimensions, and a variant from a backend without the field has no class: neither gets a transit
     window, so the schema says nothing rather than guess. */
  const { shippingClass } = variant;
  const transitDays = shippingClass && shippingClass !== 'quoted' ? TRANSIT_DAYS[shippingClass] : undefined;
  return {
    '@type': 'Offer',
    // FLIGHT stairs publish no delivery time, matching the PDP (see PRODUCTS_WITHOUT_LEAD_TIME).
    ...(showsLeadTime(product.type) && {
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'US',
          ...(shippingClass === 'freight' && { addressRegion: CONTIGUOUS_US_STATES }),
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          // Lead time is how long Viewrail takes to ship, not how long the carrier takes. Without one,
          // publish nothing rather than an empty value.
          ...(variant?.leadTime && {
            handlingTime: {
              '@type': 'QuantitativeValue',
              minValue: variant.leadTime,
              maxValue: variant.leadTime,
              unitCode: 'd',
            },
          }),
          ...(transitDays && {
            transitTime: {
              '@type': 'QuantitativeValue',
              minValue: transitDays.min,
              maxValue: transitDays.max,
              unitCode: 'd',
            },
          }),
          businessDays: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: [
              'https://schema.org/Monday',
              'https://schema.org/Tuesday',
              'https://schema.org/Wednesday',
              'https://schema.org/Thursday',
              'https://schema.org/Friday',
            ],
          },
        },
      },
    }),
    ...(origin && product.slug && { url: `${origin}${variantPath(product.slug, variant.sku)}` }),
    priceCurrency: product?.price?.currencyCode ?? DEFAULT_CURRENCY,
    price: (finalBasePrice ?? 0) / 100,
    availability: variant.isAvailable ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    ...(returnPolicy && { hasMerchantReturnPolicy: returnPolicy }),
  };
};

export const mapProductReview = (reviews: ReviewListProps): Product['review'] => {
  return reviews.reviews.map((review) => ({
    '@type': 'Review',
    // ISO 8601 date format 2025-04-28
    datePublished: review.createdAt,
    reviewBody: review.description,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating,
    },
    author: {
      '@type': 'Person',
      name: review.author,
    },
  }));
};
